import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const FooterIcon = ({ type }: { type: string }): React.ReactElement => {
  const icons: Record<string, React.ReactElement> = {
    phone: (
      <svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <defs>
          <linearGradient id="fp1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#7DBFF5"/><stop offset="100%" stopColor="#2478C8"/></linearGradient>
          <filter id="fpg"><feGaussianBlur stdDeviation="2" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
        </defs>
        <rect x="24" y="14" width="32" height="52" rx="7" fill="none" stroke="url(#fp1)" strokeWidth="2" opacity="0.9" filter="url(#fpg)"/>
        <rect x="24" y="14" width="32" height="52" rx="7" fill="#4A9EE8" opacity="0.08"/>
        <line x1="24" y1="24" x2="56" y2="24" stroke="#4A9EE8" strokeWidth="1" opacity="0.5"/>
        <line x1="24" y1="56" x2="56" y2="56" stroke="#4A9EE8" strokeWidth="1" opacity="0.5"/>
        <circle cx="40" cy="61" r="3" fill="#7DBFF5" opacity="0.8" filter="url(#fpg)"/>
        <rect x="34" y="18" width="12" height="3" rx="1.5" fill="#4A9EE8" opacity="0.5"/>
        <circle cx="40" cy="38" r="7" fill="none" stroke="#7DBFF5" strokeWidth="1.8" opacity="0.8" filter="url(#fpg)"/>
        <circle cx="40" cy="38" r="3" fill="#4A9EE8" opacity="0.5"/>
      </svg>
    ),
    email: (
      <svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <defs>
          <linearGradient id="fe1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#7DBFF5"/><stop offset="100%" stopColor="#2478C8"/></linearGradient>
          <filter id="feg"><feGaussianBlur stdDeviation="2" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
        </defs>
        <rect x="10" y="24" width="60" height="38" rx="6" fill="none" stroke="url(#fe1)" strokeWidth="2" opacity="0.9" filter="url(#feg)"/>
        <rect x="10" y="24" width="60" height="38" rx="6" fill="#4A9EE8" opacity="0.07"/>
        <polyline points="10,24 40,46 70,24" fill="none" stroke="#7DBFF5" strokeWidth="2" opacity="0.8" filter="url(#feg)"/>
        <circle cx="40" cy="46" r="3.5" fill="#7DBFF5" opacity="0.9" filter="url(#feg)"/>
        <circle cx="10" cy="24" r="2.5" fill="#7DBFF5" opacity="0.8"/>
        <circle cx="70" cy="24" r="2.5" fill="#4A9EE8" opacity="0.7"/>
      </svg>
    ),
    location: (
      <svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <defs>
          <linearGradient id="fl1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#7DBFF5"/><stop offset="100%" stopColor="#2478C8"/></linearGradient>
          <filter id="flg"><feGaussianBlur stdDeviation="2" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
        </defs>
        <path d="M40 10 Q54 10 54 26 Q54 42 40 64 Q26 42 26 26 Q26 10 40 10 Z" fill="none" stroke="url(#fl1)" strokeWidth="2" opacity="0.9" filter="url(#flg)"/>
        <path d="M40 10 Q54 10 54 26 Q54 42 40 64 Q26 42 26 26 Q26 10 40 10 Z" fill="#4A9EE8" opacity="0.08"/>
        <circle cx="40" cy="26" r="7" fill="none" stroke="#7DBFF5" strokeWidth="1.8" opacity="0.8"/>
        <circle cx="40" cy="26" r="3.5" fill="#7DBFF5" opacity="0.95" filter="url(#flg)"/>
        <ellipse cx="40" cy="68" rx="12" ry="3" fill="#4A9EE8" opacity="0.15"/>
      </svg>
    ),
    linkedin: (
      <svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <defs>
          <linearGradient id="li1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#7DBFF5"/><stop offset="100%" stopColor="#2478C8"/></linearGradient>
          <filter id="lig"><feGaussianBlur stdDeviation="2" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
        </defs>
        <rect x="14" y="14" width="52" height="52" rx="10" fill="none" stroke="url(#li1)" strokeWidth="2" opacity="0.9" filter="url(#lig)"/>
        <rect x="14" y="14" width="52" height="52" rx="10" fill="#4A9EE8" opacity="0.08"/>
        <circle cx="28" cy="30" r="4" fill="#7DBFF5" opacity="0.9" filter="url(#lig)"/>
        <rect x="24" y="36" width="8" height="22" rx="2" fill="#4A9EE8" opacity="0.7"/>
        <rect x="36" y="36" width="8" height="22" rx="2" fill="#4A9EE8" opacity="0.7"/>
        <path d="M44 42 Q44 36 52 36 Q58 36 58 44 L58 58" fill="none" stroke="#7DBFF5" strokeWidth="7" strokeLinecap="round" opacity="0.7"/>
      </svg>
    ),
    twitter: (
      <svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <defs>
          <linearGradient id="tw1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#7DBFF5"/><stop offset="100%" stopColor="#2478C8"/></linearGradient>
          <filter id="twg"><feGaussianBlur stdDeviation="2" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
        </defs>
        <rect x="14" y="14" width="52" height="52" rx="10" fill="none" stroke="url(#tw1)" strokeWidth="2" opacity="0.9" filter="url(#twg)"/>
        <rect x="14" y="14" width="52" height="52" rx="10" fill="#4A9EE8" opacity="0.08"/>
        <path d="M62 26 Q57 28 53 28 Q57 24 56 20 Q52 23 47 24 Q44 20 39 21 Q34 22 33 27 Q33 30 34 32 Q25 31 19 24 Q17 29 20 34 Q18 34 16 33 Q16 37 20 40 Q18 40 17 39 Q18 43 23 44 Q20 46 17 45 Q20 50 27 50 Q22 54 14 54 Q22 58 32 57 Q45 57 52 49 Q58 42 59 32 Q62 30 64 27 Z"
          fill="#7DBFF5" opacity="0.85" filter="url(#twg)"/>
        <circle cx="57" cy="24" r="3" fill="#4A9EE8" opacity="0.7"/>
      </svg>
    ),
    instagram: (
      <svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <defs>
          <linearGradient id="ig1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#7DBFF5"/><stop offset="100%" stopColor="#2478C8"/></linearGradient>
          <filter id="igg"><feGaussianBlur stdDeviation="2" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
        </defs>
        <rect x="14" y="14" width="52" height="52" rx="14" fill="none" stroke="url(#ig1)" strokeWidth="2" opacity="0.9" filter="url(#igg)"/>
        <rect x="14" y="14" width="52" height="52" rx="14" fill="#4A9EE8" opacity="0.08"/>
        <circle cx="40" cy="40" r="12" fill="none" stroke="#7DBFF5" strokeWidth="2" opacity="0.85" filter="url(#igg)"/>
        <circle cx="40" cy="40" r="5" fill="#4A9EE8" opacity="0.5"/>
        <circle cx="54" cy="26" r="3.5" fill="#7DBFF5" opacity="0.9" filter="url(#igg)"/>
      </svg>
    ),
  };
  return icons[type] || icons['phone'];
};

export default function Footer() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

 const quickLinks = [
  { name: t('nav.home'), path: '/' },
  { name: t('nav.about'), path: '/about' },
  { name: t('nav.services'), path: '/products' },
  { name: t('nav.contact'), path: '/contact' },
];

  const socials = [
    { href: 'https://www.linkedin.com/company/122414163', iconType: 'linkedin' },
    { href: 'https://x.com/alebel_in', iconType: 'twitter' },
    { href: 'https://www.instagram.com/alebel_energy/', iconType: 'instagram' },
  ];

  const contacts = [
  { href: 'tel:+971 4 888 6288', iconType: 'phone', label: t('cta.phone'), value: '+971 4 888 6288' },
  { href: 'mailto:info@alebelenergy.com', iconType: 'email', label: t('cta.email'), value: 'info@alebelenergy.com' },
{ href: '#', iconType: 'location', label: t('cta.location'), value: t('contact.address.short') },];

  return (
    <footer className="relative pt-14 pb-8 overflow-hidden" dir={isRTL ? 'rtl' : 'ltr'}
      style={{ backgroundColor: 'rgba(220,235,255,0.82)' }}>

      {/* Top border */}
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(to right, transparent, rgba(74,158,232,0.3), transparent)' }} />

      <div className="max-w-[1400px] mx-auto px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-20">

          {/* Brand */}
          <div className="lg:col-span-5">
            <Link to="/" className="inline-block mb-8">
              <img src="/logo-energy.png" alt="Alebel Energy" className="h-28 w-auto"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            </Link>
            <p className="mb-10 leading-relaxed text-lg max-w-sm" style={{ color: '#1a202c' }}>{t('footer.description')}</p>
            <div className="flex gap-4">
              {socials.map((s) => (
                <a key={s.iconType} href={s.href} target="_blank" rel="noopener noreferrer"
                  className="w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300 p-2.5"
                  style={{ border: '1px solid rgba(74,158,232,0.4)', backgroundColor: 'rgba(13,33,55,0.85)' }}>
                  <FooterIcon type={s.iconType} />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-2">
            <h3 className="text-sm font-bold tracking-widest uppercase mb-8" style={{ color: '#4A9EE8' }}>{t('footer.quickLinks')}</h3>
            <ul className="space-y-5">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link to={link.path}
                    className="text-base flex items-center gap-2 group transition-colors duration-300"
                    style={{ color: '#4a5568' }}>
                    {!isRTL && <span className="w-4 h-px transition-all duration-300 group-hover:w-6" style={{ backgroundColor: '#4A9EE8' }} />}
<span className="group-hover:text-blue-600 transition-colors">{link.name}</span>
{isRTL && <span className="w-4 h-px transition-all duration-300 group-hover:w-6" style={{ backgroundColor: '#4A9EE8' }} />}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-5">
            <h3 className="text-sm font-bold tracking-widest uppercase mb-8" style={{ color: '#4A9EE8' }}>{t('footer.contact')}</h3>
            <ul className="space-y-6">
              {contacts.map((item) => (
                <li key={item.label}>
                 <a href={item.href}
  className="flex items-start gap-4 transition-colors group"
  style={{ color: '#4a5568', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
  <div className="w-12 h-12 flex-shrink-0 p-1.5 rounded-xl transition-colors"
    style={{ border: '1px solid rgba(74,158,232,0.4)', backgroundColor: 'rgba(13,33,55,0.85)' }}>
    <FooterIcon type={item.iconType} />
  </div>
  <div className="pt-1 w-full" style={{ textAlign: isRTL ? 'right' : 'left' }}>
  <div className="text-xs mb-1 tracking-wider uppercase" style={{ color: '#4A9EE8' }}>{item.label}</div>
<div className="text-base" dir="ltr" style={{ color: '#2d3748', textAlign: isRTL ? 'right' : 'left' }}>{item.value}</div></div>
</a>
                </li>
              ))}
            </ul>
          </div>

        </div>

        {/* Divider */}
        <div className="h-px mb-8" style={{ background: 'linear-gradient(to right, transparent, rgba(74,158,232,0.2), transparent)' }} />

        {/* Bottom */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm" style={{ color: '#718096' }}>{t('footer.copyright')}</p>
          <div className="flex gap-8">
            <a href="#" className="text-sm transition-colors" style={{ color: '#718096' }}>{t('footer.privacy')}</a>
            <a href="#" className="text-sm transition-colors" style={{ color: '#718096' }}>{t('footer.terms')}</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
